import React, { useRef, useState, useEffect } from 'react';
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { ErrorMessage, Field, Form as FormikForm, Formik, useField } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Verified from 'assets/images/verified.gif';
import DashboardHttp from '../services/profile.service';
import OtpWithTimer from 'shared/common/otpWithTimer/otpWithTimer';
import OtpModal from 'shared/common/modal/otp_modal';
import Alert from 'shared/common/alert';
import { netcoreTrack, netcoreDate } from 'utils/netcoreTracking';

const MOBILE_REGEX = /^[789]\d{9}$/;
const NAME_REGEX = /^[A-Za-z\s]+$/;

// Must match the relation enum accepted by the account API.
const RELATIONS = [
  'Wife',
  'Husband',
  'Father',
  'Mother',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
];

const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(NAME_REGEX, 'First Name is not valid')
    .required('First Name is required'),
  last_name: Yup.string()
    .matches(NAME_REGEX, 'Last Name is not valid')
    .required('Last Name is required'),
  age: Yup.number()
    .typeError('Age must be a number')
    .integer('Age must be a whole number')
    .min(18, 'Must be at least 18 years old')
    .max(99, 'Cannot exceed 99 years')
    .required('Age is required'),
  relation: Yup.string()
    .oneOf(RELATIONS, 'Select a valid relation')
    .required('Relation is required'),
  mobile_no: Yup.string()
    .matches(MOBILE_REGEX, 'Enter a valid 10-digit mobile number')
    .required('Mobile number is required'),
  email: Yup.string().email('Invalid email').required('Email is Required'),
});

// Only these fields ever go to the API (no form-only state), trimmed and typed
// the way the API schema expects.
const buildNomineePayload = (values: any) => ({
  first_name: String(values?.first_name ?? '').trim(),
  last_name: String(values?.last_name ?? '').trim(),
  age: String(values?.age ?? '').trim(),
  relation: String(values?.relation ?? ''),
  mobile_no: String(values?.mobile_no ?? '').trim(),
  email: String(values?.email ?? '').trim().toLowerCase(),
});

// The OTP APIs echo the SMS gateway's `status`, which can be absent, and
// return status 'error' (with no otpId) when sending fails. So a send or
// resend worked when an otpId came back and status is not 'error'.
const otpWasSent = (resp: any): boolean => {
  const data = resp?.data?.data;
  return !!data?.otpId && data?.status !== 'error';
};

// API errors arrive as { data: { errors: [{ title }] } }; network failures and
// thrown errors do not, so every handler reads the message through here.
const getApiError = (err: any, fallback = 'Something went wrong'): string =>
  err?.data?.errors?.[0]?.title ||
  err?.data?.message ||
  err?.response?.data?.errors?.[0]?.title ||
  fallback;

// One labelled Formik input. `allow` filters keystrokes (e.g. letters only).
const FormField = ({
  label,
  name,
  allow,
  append,
  children,
  ...inputProps
}: any) => {
  const [, , helpers] = useField(name);
  const onChange = (e: React.ChangeEvent<any>) => {
    const { value } = e.target;
    if (!allow || allow.test(value)) helpers.setValue(value);
  };
  const input = (
    <Field
      name={name}
      className='form-control form-control1'
      {...inputProps}
      onChange={onChange}
      onBlur={() => helpers.setTouched(true)}
    >
      {children}
    </Field>
  );
  return (
    <Form.Group className='mb-3' controlId={`nominee-${name}`}>
      <Form.Label>{label}</Form.Label>
      {append ? (
        <InputGroup className='mb-2'>
          {input}
          {append}
        </InputGroup>
      ) : (
        input
      )}
      <div className='error text-danger ml-2'>
        <ErrorMessage name={name}>
          {(msg) => <span className='error'>{msg}</span>}
        </ErrorMessage>
      </div>
    </Form.Group>
  );
};

interface NomineeFormProps {
  nominee?: any; // existing nominee; undefined when adding a new one
  customerId: any;
  user: any;
  onSaved: () => Promise<void> | void;
  onCancel?: () => void;
}

// One nominee form that handles viewing, editing and adding. Saving always
// goes through two OTPs: the nominee mobile (only when it is new or changed)
// and the customer's own consent OTP.
const NomineeForm = ({
  nominee,
  customerId,
  user,
  onSaved,
  onCancel,
}: NomineeFormProps) => {
  const isNew = !nominee;
  const [isEditing, setIsEditing] = useState(isNew);
  const [confirmed, setConfirmed] = useState(false);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);

  // Nominee mobile OTP.
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const [mobileOtp, setMobileOtp] = useState({ id: null, mobile: '', sent: false });
  const [mobileOtpValue, setMobileOtpValue] = useState('');

  // Customer consent OTP.
  const [consent, setConsent] = useState({ id: null, show: false, error: '' });
  const [consentLoading, setConsentLoading] = useState(false);

  // Ref guards double clicks inside async handlers.
  const busyRef = useRef(false);

  const initialValues = {
    first_name: nominee?.firstName ?? '',
    last_name: nominee?.lastName ?? '',
    age: nominee?.age ?? '',
    relation: nominee?.relation ?? '',
    mobile_no: nominee?.mobileNo ?? '',
    email: nominee?.email ?? '',
  };

  // A mobile is accepted when it passed OTP in this session, or when it is
  // the nominee's existing number (unchanged on edit).
  const isMobileAccepted = (mobile: string) =>
    mobile !== '' &&
    (mobile === verifiedMobile ||
      (!isNew && mobile === String(nominee.mobileNo)));

  const resetFlow = () => {
    setConfirmed(false);
    setVerifiedMobile('');
    setMobileOtp({ id: null, mobile: '', sent: false });
    setMobileOtpValue('');
    setConsent({ id: null, show: false, error: '' });
  };

  const runOtpAction = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await action();
    } finally {
      busyRef.current = false;
    }
  };

  const sendMobileOtp = (values: any) =>
    runOtpAction(async () => {
      if (!validationSchema.isValidSync(values)) {
        toast.error('Please correct the highlighted fields first');
        return;
      }
      const payload = buildNomineePayload(values);
      try {
        const resp: any = await DashboardHttp.sendOtp(
          { ...payload, customer_id: String(customerId) },
          user?.userId
        );
        if (otpWasSent(resp)) {
          setMobileOtp({
            id: resp.data.data.otpId,
            mobile: payload.mobile_no,
            sent: true,
          });
          toast.success('Otp sent successfully!');
        } else {
          toast.error('Unable to send OTP. Please try again');
        }
      } catch (err: any) {
        toast.error(getApiError(err));
      }
    });

  const resendMobileOtp = async () => {
    try {
      const resp: any = await DashboardHttp.resendOtp({ otpId: mobileOtp.id });
      if (otpWasSent(resp)) {
        toast.success('Otp sent successfully!');
        setMobileOtpValue('');
      } else {
        toast.error('Unable to resend OTP. Please try again');
      }
    } catch (err: any) {
      toast.error(getApiError(err));
    }
  };

  const verifyMobileOtp = () =>
    runOtpAction(async () => {
      if (!/^\d{6}$/.test(mobileOtpValue)) {
        toast.error('Please enter the 6-digit OTP');
        return;
      }
      try {
        const resp: any = await DashboardHttp.verifyOtp(
          { otp: Number(mobileOtpValue), otpId: mobileOtp.id },
          user?.userId
        );
        if (resp?.data?.data?.optVerified) {
          // Remember exactly which number passed OTP; saving re-checks it.
          setVerifiedMobile(mobileOtp.mobile);
          setMobileOtp({ id: null, mobile: '', sent: false });
          toast.success('Mobile number is verified successfully!');
        } else {
          toast.error('OTP verification failed. Please try again');
        }
      } catch (err: any) {
        toast.error(getApiError(err, 'OTP verification failed'));
      }
      setMobileOtpValue('');
    });

  const sendConsentOtp = () =>
    runOtpAction(async () => {
      if (!user?.mobile?.value || !user?.userId) {
        toast.error('Your mobile number is not available. Please re-login');
        return;
      }
      try {
        const resp: any = await DashboardHttp.sendCustomerOtp({
          mobile_no: user.mobile.value,
          user_id: user.userId,
        });
        if (otpWasSent(resp)) {
          setConsent({ id: resp.data.data.otpId, show: true, error: '' });
          toast.success('Otp sent successfully!');
        } else {
          toast.error('Unable to send OTP. Please try again');
        }
      } catch (err: any) {
        toast.error(getApiError(err));
      }
    });

  const resendConsentOtp = async () => {
    try {
      const resp: any = await DashboardHttp.resendCustomerOtp(
        { otpId: consent.id },
        user?.userId
      );
      if (otpWasSent(resp)) toast.success('Otp sent successfully!');
      else toast.error('Unable to resend OTP. Please try again');
    } catch (err: any) {
      toast.error(getApiError(err));
    }
  };

  // Saving is only reachable from the customer consent OTP success.
  const save = async (values: any) => {
    const payload = buildNomineePayload(values);
    if (!customerId) {
      toast.error('Customer not found. Please refresh and try again');
      return false;
    }
    if (!isMobileAccepted(payload.mobile_no)) {
      toast.error("Please verify nominee's mobile number");
      return false;
    }
    try {
      if (isNew) {
        const resp: any = await DashboardHttp.addNominee({
          ...payload,
          customer_id: String(customerId),
          is_mobile_verified: true,
          is_customer_mobile_verified: true,
        });
        if (!resp?.data) throw new Error('Empty response');
        toast.success('Nominee added successfully');
        netcoreTrack(
          'nominee_add',
          {
            customer_id: customerId,
            relationship: payload.relation,
            new_created_at: netcoreDate(new Date()),
          },
          user?.mobile?.value,
          { HAS_NOMINEE: true }
        );
      } else {
        const resp: any = await DashboardHttp.updateNominee(
          {
            ...payload,
            id: String(nominee.id),
            customer_id: String(customerId),
            is_customer_mobile_verified: true,
          },
          user?.userId
        );
        if (!resp?.data) throw new Error('Empty response');
        toast.success('Nominee Details Updated Successfully!');
      }
      return true;
    } catch (err: any) {
      toast.error(getApiError(err));
      return false;
    }
  };

  const verifyConsentOtp = async (otp: string, values: any) => {
    if (consentLoading) return;
    setConsentLoading(true);
    try {
      const resp: any = await DashboardHttp.verifyCustomerOtp(
        { otp: Number(otp), otpId: consent.id },
        user?.userId
      );
      if (!resp?.data?.data?.optVerified) {
        setConsent({ ...consent, error: 'OTP verification failed. Please try again' });
        return;
      }
      setConsent({ id: null, show: false, error: '' });
      if (await save(values)) {
        resetFlow();
        setIsEditing(isNew);
        await onSaved();
      }
    } catch (err: any) {
      const message = getApiError(err, 'OTP verification failed');
      toast.error(message);
      setConsent({ ...consent, error: message });
    } finally {
      setConsentLoading(false);
    }
  };

  const cancel = (resetForm: () => void) => {
    resetFlow();
    resetForm();
    setIsEditing(false);
  };

  return (
    <Accordion className='mt-4 mb-4' defaultActiveKey='0'>
      <Accordion.Item eventKey='0'>
        <Accordion.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            // Saving goes through the OTP consent modal only (no submit-on-Enter).
            onSubmit={() => undefined}
          >
            {({ values, resetForm }) => {
              const formValid = validationSchema.isValidSync(values);
              const mobileAccepted = isMobileAccepted(values.mobile_no);
              const showVerifyButton =
                isEditing &&
                MOBILE_REGEX.test(values.mobile_no) &&
                !mobileAccepted &&
                !mobileOtp.sent;
              const showVerifiedIcon =
                mobileAccepted &&
                (values.mobile_no === verifiedMobile || nominee?.isMobileVerified);

              return (
                <FormikForm>
                  <Card.Body className='p-md-5 p-sm-2 pb-0'>
                    <Row>
                      <Col md={6}>
                        <FormField
                          label='First Name'
                          name='first_name'
                          type='text'
                          maxLength={20}
                          placeholder='First Name'
                          allow={/^[A-Za-z\s]*$/}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col md={6}>
                        <FormField
                          label='Last Name'
                          name='last_name'
                          type='text'
                          maxLength={20}
                          placeholder='Last Name'
                          allow={/^[A-Za-z\s]*$/}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col md={6}>
                        <FormField
                          label='Relation'
                          name='relation'
                          as='select'
                          className='form-control'
                          disabled={!isEditing}
                        >
                          <option value=''>Select an option</option>
                          {RELATIONS.map((relation) => (
                            <option key={relation} value={relation}>
                              {relation}
                            </option>
                          ))}
                        </FormField>
                      </Col>
                      <Col md={6}>
                        <FormField
                          label='Age'
                          name='age'
                          type='number'
                          min={18}
                          max={99}
                          placeholder='Age'
                          allow={/^([1-9]\d?)?$/}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col md={6}>
                        <FormField
                          label='Email'
                          name='email'
                          type='text'
                          placeholder='Email'
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col md={6}>
                        <FormField
                          label='Mobile'
                          name='mobile_no'
                          type='text'
                          maxLength={10}
                          placeholder='Mobile'
                          // Digits only, starting with 7, 8 or 9.
                          allow={/^([789]\d{0,9})?$/}
                          disabled={!isEditing}
                          append={
                            <>
                              {showVerifyButton && (
                                <div className='input-group-text'>
                                  <button
                                    className='btn btn-verify'
                                    type='button'
                                    style={{ borderRadius: '0 5px 5px 0' }}
                                    onClick={() => sendMobileOtp(values)}
                                  >
                                    Verify
                                  </button>
                                </div>
                              )}
                              {showVerifiedIcon && (
                                <div className='input-group-text px-2'>
                                  <img
                                    src={Verified}
                                    alt='Verified'
                                    width='22px'
                                    height='22px'
                                  />
                                </div>
                              )}
                            </>
                          }
                        />
                        {mobileOtp.sent && (
                          <>
                            <OtpWithTimer
                              enableResendTimer
                              resendInterval={30}
                              numInputs={6}
                              otpLabel='Please enter OTP'
                              otpChangeHandler={setMobileOtpValue}
                              isCentered
                              otpValue={mobileOtpValue}
                              resendButtonText='Resend OTP'
                              ackMessage='OTP has been sent to the mobile number.'
                              ackMessageType='success'
                              resendHandler={resendMobileOtp}
                            />
                            <Button
                              variant='primary'
                              className='mb-5'
                              onClick={verifyMobileOtp}
                            >
                              Verify
                            </Button>
                          </>
                        )}
                      </Col>

                      {isEditing && (
                        <>
                          <Col md={12}>
                            <div className='form-group mb-2'>
                              <label className='form-label'>
                                <input
                                  type='checkbox'
                                  className='mx-1'
                                  checked={confirmed}
                                  disabled={!formValid || !mobileAccepted}
                                  onChange={(e) => setConfirmed(e.target.checked)}
                                />
                                I confirm the nominee details provided above are
                                correct and should be put in records.
                              </label>
                            </div>
                          </Col>
                          <div className='text-center'>
                            {confirmed && formValid && mobileAccepted && (
                              <Button className='w-30' onClick={sendConsentOtp}>
                                Confirm
                              </Button>
                            )}
                            <Button
                              variant='secondary'
                              className='m-3'
                              onClick={() =>
                                isNew
                                  ? setShowDiscardAlert(true)
                                  : cancel(resetForm)
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}

                      {consent.show && (
                        <Col md={6}>
                          <OtpModal
                            show={consent.show}
                            handleClose={() =>
                              setConsent({ ...consent, show: false })
                            }
                            title='OTP is sent to your registered mobile number'
                            onSuccess={(otp) => verifyConsentOtp(otp, values)}
                            resend={resendConsentOtp}
                            txtError={consent.error}
                            loading={consentLoading}
                          />
                        </Col>
                      )}
                    </Row>

                    {!isEditing && (
                      <Button variant='primary' onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    )}
                  </Card.Body>
                </FormikForm>
              );
            }}
          </Formik>
        </Accordion.Body>
      </Accordion.Item>
      <Alert
        open={showDiscardAlert}
        message='Are you sure you want to close? All changes will be discarded'
        onSuccess={() => {
          setShowDiscardAlert(false);
          resetFlow();
          onCancel?.();
        }}
        onCancel={() => setShowDiscardAlert(false)}
      />
    </Accordion>
  );
};

const Nomination = ({ customerId, setCustomer, user }: any) => {
  const [nominees, setNominees] = useState<any[]>([]);
  const [isAddNominee, setIsAddNominee] = useState(false);

  const getNominees = async () => {
    try {
      const userData: any = await DashboardHttp.getProfile(
        customerId,
        user?.userId
      );
      const profile = userData?.data?.data?.customer;
      if (!profile) return;
      setCustomer(profile);
      setNominees(
        Array.isArray(profile.nomineeDetails) ? profile.nomineeDetails : []
      );
    } catch (err: any) {
      toast.error(getApiError(err));
    }
  };

  useEffect(() => {
    if (customerId) getNominees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleSaved = async () => {
    setIsAddNominee(false);
    await getNominees();
  };

  return (
    <Card>
      <Card.Body className='p-md-5 p-sm-2 pb-0'>
        <div className='d-flex justify-content-between'>
          <Card.Title>Nominee Details</Card.Title>
          {nominees.length === 0 && !isAddNominee && (
            <Button className='btn btn-sm' onClick={() => setIsAddNominee(true)}>
              + Add Nominee
            </Button>
          )}
        </div>
        {nominees.map((nominee) => (
          <NomineeForm
            key={nominee.id}
            nominee={nominee}
            customerId={customerId}
            user={user}
            onSaved={handleSaved}
          />
        ))}
        {isAddNominee && (
          <NomineeForm
            customerId={customerId}
            user={user}
            onSaved={handleSaved}
            onCancel={() => setIsAddNominee(false)}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default Nomination;
